// src/pages/B2B/event-reports/EventReportsListPage.tsx
import React from 'react';
import { Link } from 'react-router-dom';
import { useEventReports } from './api/useEventReports';
import { useDeleteEventReport } from './api/useDeleteEventReport';
import { ReportListTable } from './components/ReportListTable';
import { useReportEnv } from './api/useReportEnv';
import { Env, getAdminApiUrl, ENV_COLORS } from '@/pages/Admin/shared';

export function EventReportsListPage(): React.ReactElement {
  const [env, setEnv] = useReportEnv();
  const { items, total, loading, error, refetch } = useEventReports(env);
  const { remove, error: deleteError } = useDeleteEventReport(env);

  const handleDelete = async (id: string) => {
    if (!window.confirm(`Delete this report from ${env.toUpperCase()}? This cannot be undone.`)) return;
    const ok = await remove(id);
    if (ok) refetch();
  };

  return (
    <div className="min-h-screen bg-[#0f0f1a]">
      <div className="max-w-5xl mx-auto px-6 pt-8 pb-16">
        <div className="mb-6">
          <Link to="/b2b" className="text-sm text-gray-500 hover:text-gray-300 no-underline">
            B2B Dashboards
          </Link>
          <span className="text-gray-700 mx-2">/</span>
          <span className="text-sm text-gray-400">Event Reports</span>
        </div>

        <div className="flex items-center justify-between mb-6">
          <div>
            <div className="flex items-center gap-4">
              <h1 className="text-2xl font-bold text-white font-display">Event Reports</h1>
              <div className="flex items-center gap-1.5 p-1 bg-gray-800/80 border border-gray-700 rounded-xl">
                {(['dev', 'prod'] as Env[]).map((e) => {
                  const isConfigured = Boolean(getAdminApiUrl(e));
                  return (
                    <button
                      key={e}
                      onClick={() => isConfigured && setEnv(e)}
                      disabled={!isConfigured}
                      className={`px-3 py-1.5 text-xs font-semibold rounded-lg border transition-all cursor-pointer ${
                        env === e
                          ? ENV_COLORS[e].pill
                          : isConfigured
                            ? 'border-transparent text-gray-500 hover:text-gray-300 hover:bg-gray-700/50'
                            : 'border-transparent text-gray-700 cursor-not-allowed'
                      }`}
                    >
                      {e.toUpperCase()}
                    </button>
                  );
                })}
              </div>
            </div>
            <p className="mt-1 text-sm text-gray-400">
              Generate partner-shareable PDF + CSV recaps for a date/time window. {total} {total === 1 ? 'report' : 'reports'}.
            </p>
          </div>
          <Link
            to="/b2b/event-reports/new"
            className="px-4 py-2 text-sm font-semibold text-white bg-violet-600 border border-violet-500 rounded-lg hover:bg-violet-500 no-underline"
          >
            + New Report
          </Link>
        </div>

        {(error || deleteError) && (
          <div className="mb-6 rounded-xl border border-red-700/40 bg-red-950/40 px-4 py-3 text-sm text-red-300">
            {error || deleteError}
          </div>
        )}

        <ReportListTable items={items} loading={loading} onDelete={handleDelete} />
      </div>
    </div>
  );
}

export default EventReportsListPage;
